import { Linkify } from "@/components/Linkify";
import React, { useEffect, useState } from "react";
import { clientApi, publicApi, formatApiError } from "@/lib/http";
import { fmtCurrency } from "@/lib/calc";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CalendarDays, Clock, Wallet, Package, X, LogOut, RotateCcw, CreditCard, Plus, Trash2, FileText, Receipt, TrendingUp, HeartPulse, Lock, ChevronRight } from "lucide-react";

const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

// ---------- Sign in / Create account dialog ----------
export function ClientAuthDialog({ slug, open, onOpenChange, onAuthed }) {
  const [tab, setTab] = useState("login");
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(null); // null | "phone" | "code"
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sendCode = async () => {
    setBusy(true);
    try { await publicApi.post("/public/client/forgot-password", { slug, phone: form.phone }); setForgot("code"); toast.success("If there's an account for that mobile, we've texted a code"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send a code"); }
    finally { setBusy(false); }
  };
  const resetPw = async () => {
    setBusy(true);
    try {
      const { data } = await publicApi.post("/public/client/reset-password", { slug, phone: form.phone, code: form.code, new_password: form.password });
      localStorage.setItem("pc_client_token", data.token); onAuthed(data.client); onOpenChange(false); setForgot(null);
      toast.success("Password updated — you're signed in");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "That code isn't right"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const path = tab === "login" ? "/public/client/login" : "/public/client/register";
      const payload = tab === "login"
        ? { slug, phone: form.phone, password: form.password }
        : { slug, name: form.name, phone: form.phone, email: form.email, password: form.password };
      const { data } = await publicApi.post(path, payload);
      localStorage.setItem("pc_client_token", data.token);
      onAuthed(data.client);
      onOpenChange(false);
      toast.success(tab === "login" ? "Welcome back!" : "Account created — you're signed in");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d && typeof d === "object" && d.code === "banned" ? d.message : (formatApiError(d) || "Something went wrong"), d?.code === "banned" ? { duration: 12000 } : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="client-auth-dialog">
        <DialogHeader><DialogTitle className="font-heading">My account</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Sign in or create an account with your mobile number and password.</DialogDescription>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="client-tab-login">Sign in</TabsTrigger>
            <TabsTrigger value="register" data-testid="client-tab-register">Create account</TabsTrigger>
          </TabsList>
          <TabsContent value="login" className="space-y-3 mt-4">
            <Input placeholder="Mobile number" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11 rounded-lg" data-testid="client-login-phone" />
            {!forgot && <Input type="password" placeholder="Password" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-11 rounded-lg" data-testid="client-login-password" />}
            {forgot === "code" && (
              <>
                <Input inputMode="numeric" placeholder="6-digit code from your text" value={form.code} onChange={(e) => set("code", e.target.value)} className="h-11 rounded-lg tracking-widest" data-testid="client-reset-code" />
                <Input type="password" placeholder="New password (min 6 characters)" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-11 rounded-lg" data-testid="client-reset-password" />
              </>
            )}
            <div className="flex justify-between text-xs">
              {forgot
                ? <button type="button" onClick={() => setForgot(null)} className="text-muted-foreground hover:underline" data-testid="client-forgot-back">Back to sign in</button>
                : <button type="button" onClick={() => setForgot("phone")} className="text-primary hover:underline" data-testid="client-forgot-link">Forgot password?</button>}
              {forgot === "code" && <button type="button" onClick={sendCode} disabled={busy} className="text-muted-foreground hover:underline" data-testid="client-resend-code">Resend code</button>}
            </div>
          </TabsContent>
          <TabsContent value="register" className="space-y-3 mt-4">
            <Input placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 rounded-lg" data-testid="client-reg-name" />
            <Input placeholder="Mobile number" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11 rounded-lg" data-testid="client-reg-phone" />
            <Input placeholder="Email (optional)" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 rounded-lg" data-testid="client-reg-email" />
            <Input type="password" placeholder="Password (min 6 characters)" value={form.password} onChange={(e) => set("password", e.target.value)} className="h-11 rounded-lg" data-testid="client-reg-password" />
          </TabsContent>
        </Tabs>
        {tab === "login" && forgot === "phone" ? (
          <Button className="w-full h-11 rounded-lg mt-2" disabled={busy || !form.phone} onClick={sendCode} data-testid="client-forgot-send">Text me a reset code</Button>
        ) : tab === "login" && forgot === "code" ? (
          <Button className="w-full h-11 rounded-lg mt-2" disabled={busy || !form.phone || form.code.length < 6 || form.password.length < 6} onClick={resetPw} data-testid="client-reset-submit">Set new password & sign in</Button>
        ) : (
          <Button className="w-full h-11 rounded-lg mt-2" disabled={busy || !form.phone || !form.password || (tab === "register" && !form.name)} onClick={submit} data-testid="client-auth-submit">
            {tab === "login" ? "Sign in" : "Create account"}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Reschedule dialog ----------
export function RescheduleDialog({ slug, booking, open, onOpenChange, onDone }) {
  const [date, setDate] = useState(todayStr());
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !booking) return;
    setSlots([]);
    publicApi.get("/public/availability", { params: { slug, service_id: booking.service_id, staff_id: booking.staff_id, date } })
      .then(({ data }) => setSlots(data.slots || [])).catch(() => setSlots([]));
  }, [open, date, booking, slug]);

  const pick = async (start) => {
    setBusy(true);
    try {
      await clientApi.put(`/public/client/bookings/${booking.id}/reschedule`, { start });
      toast.success("Appointment moved");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't reschedule");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="client-reschedule-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reschedule {booking?.service_name}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Choose a new date and time for your appointment.</DialogDescription>
        <div className="flex items-center gap-2 mb-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <Input type="date" value={date} min={todayStr()} onChange={(e) => setDate(e.target.value)} className="h-10 rounded-lg w-44" data-testid="client-reschedule-date" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
          {slots.map((s) => (
            <button key={s} disabled={busy} onClick={() => pick(s)} data-testid={`client-reschedule-slot-${s.slice(11, 16)}`}
              className="rounded-lg border border-border bg-card py-2 text-sm hover:border-primary hover:text-primary transition-colors disabled:opacity-50">{s.slice(11, 16)}</button>
          ))}
          {slots.length === 0 && <div className="col-span-full text-muted-foreground text-sm">No times available — try another day.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }) + " · " + iso.slice(11, 16);
};
const shortDate = (iso) => { if (!iso) return ""; return new Date(iso).toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit", year: "numeric" }); };


// ---------- Portal (upcoming/past bookings, credit, packages) ----------
export function ClientPortal({ slug, cur, client, onClose, onLogout }) {
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [account, setAccount] = useState({ credit_balance: 0, packages: [] });
  const [loading, setLoading] = useState(true);
  const [reschedule, setReschedule] = useState(null);
  const [cards, setCards] = useState([]);
  const [cardBusy, setCardBusy] = useState(false);
  const [forms, setForms] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [progress, setProgress] = useState({ treatments: [], packages: [] });
  const [aftercare, setAftercare] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const token = localStorage.getItem("pc_client_token");
  const photoUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/public/client/photos/${id}/file?auth=${token}`;

  const load = () => {
    setLoading(true);
    Promise.all([
      clientApi.get("/public/client/bookings"),
      clientApi.get("/public/client/account"),
    ]).then(([b, a]) => { setData(b.data); setAccount(a.data); }).finally(() => setLoading(false));
    clientApi.get("/public/client/cards").then(({ data }) => setCards(data)).catch(() => setCards([]));
    clientApi.get("/public/client/forms").then(({ data }) => setForms(data)).catch(() => {});
    clientApi.get("/public/client/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
    clientApi.get("/public/client/progress").then(({ data }) => setProgress(data)).catch(() => {});
    clientApi.get("/public/client/aftercare").then(({ data }) => setAftercare(data)).catch(() => {});
    clientApi.get("/public/client/photos").then(({ data }) => setPhotos(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("card") === "added") {
      toast.success("Card saved securely");
      sp.delete("card");
      window.history.replaceState({}, "", `${window.location.pathname}${sp.toString() ? "?" + sp.toString() : ""}`);
    }
    // eslint-disable-next-line
  }, []);

  const addCard = async () => {
    setCardBusy(true);
    try {
      const { data } = await clientApi.post("/public/client/cards/setup", { origin_url: window.location.origin, return_path: `/book/${slug}` });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start card setup"); setCardBusy(false); }
  };
  const removeCard = async (pm) => {
    if (!window.confirm("Remove this saved card?")) return;
    try { await clientApi.delete(`/public/client/cards/${pm}`); setCards((cs) => cs.filter((c) => c.id !== pm)); toast.success("Card removed"); }
    catch { toast.error("Couldn't remove card"); }
  };

  const cancel = async (b) => {
    if (!window.confirm(`Cancel your ${b.service_name} on ${fmtWhen(b.start)}?`)) return;
    try {
      await clientApi.post(`/public/client/bookings/${b.id}/cancel`);
      toast.success("Appointment cancelled");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't cancel");
    }
  };

  const changePassword = async () => {
    setPwBusy(true);
    try {
      await clientApi.post("/public/client/change-password", pw);
      toast.success("Password changed");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't change password"); }
    finally { setPwBusy(false); }
  };

  return (
    <div data-testid="client-portal">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-heading font-bold text-2xl">Hi {client.name?.split(" ")[0] || "there"}</h2>
          <p className="text-muted-foreground text-sm">Your appointments & account</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-lg" onClick={onLogout} data-testid="client-logout"><LogOut className="h-4 w-4 mr-1" />Sign out</Button>
          <Button size="sm" className="rounded-lg" onClick={onClose} data-testid="client-portal-book">Book new</Button>
        </div>
      </div>

      <Tabs defaultValue="appointments" className="w-full">
        <TabsList className="w-full flex flex-wrap h-auto gap-1 justify-start mb-4">
          <TabsTrigger value="appointments" data-testid="portal-tab-appointments"><CalendarDays className="h-3.5 w-3.5 mr-1" />Appointments</TabsTrigger>
          <TabsTrigger value="progress" data-testid="portal-tab-progress"><TrendingUp className="h-3.5 w-3.5 mr-1" />Progress</TabsTrigger>
          <TabsTrigger value="forms" data-testid="portal-tab-forms"><FileText className="h-3.5 w-3.5 mr-1" />Forms</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="portal-tab-invoices"><Receipt className="h-3.5 w-3.5 mr-1" />Invoices</TabsTrigger>
          <TabsTrigger value="aftercare" data-testid="portal-tab-aftercare"><HeartPulse className="h-3.5 w-3.5 mr-1" />Aftercare</TabsTrigger>
          <TabsTrigger value="account" data-testid="portal-tab-account"><Wallet className="h-3.5 w-3.5 mr-1" />Account</TabsTrigger>
        </TabsList>

        <TabsContent value="appointments">
          <div className="mb-6">
            <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2">Upcoming</div>
            {loading ? <div className="text-muted-foreground text-sm">Loading…</div> : data.upcoming.length === 0 ? (
              <div className="text-muted-foreground text-sm" data-testid="client-no-upcoming">No upcoming appointments.</div>
            ) : (
              <div className="space-y-2">
                {data.upcoming.map((b) => (
                  <Card key={b.id} className="rounded-xl p-4 flex items-center justify-between flex-wrap gap-2" data-testid={`client-upcoming-${b.id}`}>
                    <div>
                      <div className="font-medium">{b.service_name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><Clock className="h-3 w-3" />{fmtWhen(b.start)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setReschedule(b)} data-testid={`client-reschedule-${b.id}`}><RotateCcw className="h-4 w-4 mr-1" />Reschedule</Button>
                      <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={() => cancel(b)} data-testid={`client-cancel-${b.id}`}><X className="h-4 w-4 mr-1" />Cancel</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2">Past</div>
            {data.past.length === 0 ? (
              <div className="text-muted-foreground text-sm">No past visits yet.</div>
            ) : (
              <div className="space-y-2">
                {data.past.slice(0, 30).map((b) => (
                  <div key={b.id} className="flex items-center justify-between rounded-xl border border-border bg-card/50 px-4 py-3" data-testid={`client-past-${b.id}`}>
                    <div>
                      <div className="text-sm font-medium">{b.service_name}</div>
                      <div className="text-xs text-muted-foreground">{fmtWhen(b.start)}</div>
                    </div>
                    <span className="text-xs text-muted-foreground capitalize">{b.status === "cancelled" ? "Cancelled" : b.status === "checked_out" ? "Completed" : b.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="progress" data-testid="portal-progress">
          <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2">Sessions per treatment</div>
          {progress.treatments.length === 0 ? (
            <div className="text-muted-foreground text-sm mb-6">No completed treatments yet.</div>
          ) : (
            <div className="space-y-2 mb-6">
              {progress.treatments.map((t) => (
                <Card key={t.service} className="rounded-xl p-4 flex items-center justify-between" data-testid={`progress-${t.service}`}>
                  <div className="font-medium">{t.service}</div>
                  <div className="text-right"><div className="font-heading font-bold text-primary text-lg">{t.sessions}</div><div className="text-[11px] text-muted-foreground">session{t.sessions === 1 ? "" : "s"} done</div></div>
                </Card>
              ))}
            </div>
          )}
          {progress.packages.length > 0 && (
            <>
              <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2">Prepaid packages</div>
              <div className="space-y-2 mb-6">
                {progress.packages.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm rounded-xl border border-border px-4 py-3"><span>{p.name}</span><span className="text-muted-foreground">{p.sessions_remaining}/{p.sessions_total} left</span></div>
                ))}
              </div>
            </>
          )}
          <div className="text-xs font-mono-label uppercase tracking-wider text-muted-foreground mb-2">Before &amp; after photos</div>
          {photos.length === 0 ? (
            <div className="text-muted-foreground text-sm">No photos yet — your clinic can add progress photos during your visits.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {photos.map((p) => (
                <div key={p.id} className="rounded-xl overflow-hidden border border-border" data-testid={`photo-${p.id}`}>
                  <img src={photoUrl(p.id)} alt={p.label} className="w-full h-32 object-cover" loading="lazy" />
                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-[11px] font-medium capitalize">{p.label}</span>
                    <span className="text-[10px] text-muted-foreground">{shortDate(p.taken_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="forms" data-testid="portal-forms">
          {forms.length === 0 ? (
            <div className="text-muted-foreground text-sm">No forms to complete right now.</div>
          ) : (
            <div className="space-y-2">
              {forms.map((f) => {
                const done = f.status === "completed" || f.completed_at;
                return (
                  <a key={f.id} href={`/consult/${f.token}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-4 hover:border-primary/50 transition-colors" data-testid={`form-${f.id}`}>
                    <div>
                      <div className="font-medium flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />{f.name}</div>
                      <div className="text-xs mt-0.5">{done ? <span className="text-emerald-600">Completed</span> : <span className="text-amber-600">Needs completing</span>}</div>
                    </div>
                    <span className="text-sm text-primary flex items-center">{done ? "View" : "Complete"}<ChevronRight className="h-4 w-4" /></span>
                  </a>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invoices" data-testid="portal-invoices">
          {invoices.length === 0 ? (
            <div className="text-muted-foreground text-sm">No invoices yet.</div>
          ) : (
            <div className="space-y-3">
              {invoices.map((inv) => (
                <Card key={inv.id} className="rounded-xl p-4" data-testid={`invoice-${inv.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{shortDate(inv.created_at)}</span>
                    <span className="font-heading font-bold text-primary">{fmtCurrency(inv.total_paid, cur, 2)}</span>
                  </div>
                  <div className="space-y-0.5">
                    {(inv.line_items || []).map((li, i) => (
                      <div key={i} className="flex justify-between text-xs text-muted-foreground">
                        <span>{li.qty && li.qty !== 1 ? `${li.qty}× ` : ""}{li.name}</span>
                        <span>{fmtCurrency(li.amount, cur, 2)}</span>
                      </div>
                    ))}
                    {inv.discount > 0 && <div className="flex justify-between text-xs text-secondary"><span>Discount</span><span>− {fmtCurrency(inv.discount, cur, 2)}</span></div>}
                  </div>
                  {inv.method && <div className="text-[11px] text-muted-foreground mt-2 capitalize">Paid by {inv.method.replace("_", " ")}</div>}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="aftercare" data-testid="portal-aftercare">
          {aftercare.length === 0 ? (
            <div className="text-muted-foreground text-sm">No aftercare instructions yet.</div>
          ) : (
            <div className="space-y-3">
              {aftercare.map((a) => (
                <Card key={a.id} className="rounded-xl p-4" data-testid={`aftercare-${a.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium flex items-center gap-2"><HeartPulse className="h-4 w-4 text-primary" />{a.title}</div>
                    <span className="text-[11px] text-muted-foreground">{shortDate(a.created_at)}</span>
                  </div>
                  {a.instructions && <div className="text-sm text-muted-foreground whitespace-pre-wrap mt-1"><Linkify text={a.instructions} /></div>}
                  {a.token && <a href={`/aftercare/${a.token}`} className="text-sm text-primary inline-flex items-center mt-2">Open full instructions<ChevronRight className="h-4 w-4" /></a>}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="account" data-testid="portal-account">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <Card className="rounded-xl p-4" data-testid="client-credit">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Wallet className="h-3.5 w-3.5" />Account credit</div>
              <div className="font-heading font-bold text-2xl text-primary mt-1">{fmtCurrency(account.credit_balance, cur, 2)}</div>
            </Card>
            <Card className="rounded-xl p-4" data-testid="client-packages">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><Package className="h-3.5 w-3.5" />Packages</div>
              {account.packages.length === 0 ? (
                <div className="text-sm text-muted-foreground mt-2">None yet</div>
              ) : (
                <div className="mt-1 space-y-1">
                  {account.packages.map((p) => (
                    <div key={p.id} className="text-sm flex justify-between"><span>{p.name}</span><span className="text-muted-foreground">{p.sessions_remaining}/{p.sessions_total} left</span></div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          <Card className="rounded-xl p-4 mb-4" data-testid="client-cards">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground"><CreditCard className="h-3.5 w-3.5" />Saved cards</div>
              <Button size="sm" variant="outline" className="rounded-lg" onClick={addCard} disabled={cardBusy} data-testid="client-add-card"><Plus className="h-3.5 w-3.5 mr-1" />{cardBusy ? "Opening…" : "Add card"}</Button>
            </div>
            {cards.length === 0 ? (
              <div className="text-sm text-muted-foreground mt-2">No cards saved. Add one for faster checkout at the clinic.</div>
            ) : (
              <div className="mt-2 space-y-1.5">
                {cards.map((c) => (
                  <div key={c.id} className="flex items-center justify-between text-sm" data-testid={`client-card-${c.id}`}>
                    <span className="capitalize flex items-center gap-2"><CreditCard className="h-4 w-4 text-primary" />{c.brand} •••• {c.last4} <span className="text-muted-foreground text-xs">exp {String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span></span>
                    <button onClick={() => removeCard(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`client-card-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">Stored securely by Stripe — the clinic never sees your full card number.</p>
          </Card>

          <Card className="rounded-xl p-4" data-testid="client-change-password">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-3"><Lock className="h-3.5 w-3.5" />Change password</div>
            <div className="space-y-2 max-w-sm">
              <Input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw((s) => ({ ...s, current_password: e.target.value }))} className="h-11 rounded-lg" data-testid="pw-current" />
              <Input type="password" placeholder="New password (min 6 characters)" value={pw.new_password} onChange={(e) => setPw((s) => ({ ...s, new_password: e.target.value }))} className="h-11 rounded-lg" data-testid="pw-new" />
              <Button className="rounded-lg" disabled={pwBusy || !pw.current_password || pw.new_password.length < 6} onClick={changePassword} data-testid="pw-save">{pwBusy ? "Saving…" : "Update password"}</Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <RescheduleDialog slug={slug} booking={reschedule} open={!!reschedule} onOpenChange={(v) => !v && setReschedule(null)} onDone={load} />
    </div>
  );
}
